const { debounce, translateString } = window.BAO.Utils

class TagalysQuickSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()
    if (!window?.onTagalysReady) return

    this.initialise = this.initialise.bind(this)
    this.updateSearchSuggestions = this.updateSearchSuggestions.bind(this)
    this.recordRecentSearch = this.recordRecentSearch.bind(this)
    this.buildProductResults = this.buildProductResults.bind(this)
    this.onClearClick = this.onClearClick.bind(this)

    this.debouncedRecordRecentSearch = debounce((query) => {
      this.recordRecentSearch(query)
    }, 500)

    this._lastRecordedQuery = ''

    this.els?.input?.element.addEventListener(
      'input',
      this.updateSearchSuggestions,
    )
    this.els?.input?.element.addEventListener(
      'focus',
      this.onInputFocus?.bind(this),
    )

    this.els?.clear?.element?.addEventListener('click', this.onClearClick)
    this.els?.close?.element?.addEventListener('click', this.onClearClick)

    this.els?.input?.element?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.onClearClick(e)
    })

    // eslint-disable-next-line no-undef
    onTagalysReady(this.initialise)
  }

  initialise () {
    // eslint-disable-next-line no-undef
    window.searchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
      params: {
        request: {
          products: {
            limit: 6,
          },
          collections: {
            limit: 5,
          },
          pages: {
            limit: 6,
          },
        },
      },
      onSuccess: (response, helpers) => {
        this.buildProductResults(this.els?.products?.element, response)

        this.buildCollectionResults(this.els?.collections?.element, response)
      },
      onFailure (response, helpers) {
        console.error('Failed response:', response)
        console.error('Failed helpers:', helpers)
      },
    })
  }

  setState (state) {
    if (this.getAttribute('data-state') !== state) {
      this.setAttribute('data-state', state)
    }
  }

  async onInputFocus () {
    const helpers = window.searchSuggestions?.helpers
    if (!helpers) return

    await this.buildRecentSearches(this.els?.recents?.element, 5)
    await this.buildPopularSearches(this.els?.popular?.element, 5)
  }

  updateSearchSuggestions (event) {
    const query = event.target.value || ''
    this._currentQuery = query.trim()
    window.searchSuggestions?.helpers.updateQuery(query)
    this.debouncedRecordRecentSearch(query)

    this.els?.clear?.element.setAttribute(
      'aria-hidden',
      this._currentQuery.length < 3,
    )

    if (this._currentQuery.length < 3) {
      this.setState('default')
    }
  }

  onClearClick () {
    if (!this.els?.input?.element) return

    this.els.input.element.value = ''
    this._currentQuery = ''
    this._lastRecordedQuery = ''

    window.searchSuggestions?.helpers.updateQuery('')

    this.els.clear.element.setAttribute('aria-hidden', true)
    this.els?.products?.element && (this.els.products.element.innerHTML = '')
    this.els?.collections?.element &&
      (this.els.collections.element.innerHTML = '')

    this.els?.['products-container']?.element?.setAttribute(
      'aria-hidden',
      true,
    )
    this.els?.['suggestion-collections']?.element?.setAttribute(
      'aria-hidden',
      true,
    )
    this.els?.footer?.element?.setAttribute('aria-hidden', true)

    this.setState('default')

    this.buildRecentSearches(this.els?.recents?.element, 5)
    this.buildPopularSearches(this.els?.popular?.element, 5)

    this.els.input.element.focus()
  }

  async recordRecentSearch (rawQuery) {
    const helpers = window.searchSuggestions?.helpers
    if (!helpers?.recordRecentSearch) return

    const query = (rawQuery || '').trim()
    if (!query || query.length < 3) return
    if (query.toLowerCase() === this._lastRecordedQuery.toLowerCase()) return

    try {
      await helpers.recordRecentSearch(`q=${encodeURIComponent(query)}`)
      this._lastRecordedQuery = query
      this.buildRecentSearches(this.els?.recents?.element, 5)
    } catch (event) {
      console.error('Failed to record recent search:', event)
    }
  }

  getEncodedQueryStringFromTagalys (query) {
    const helpers = window.searchSuggestions?.helpers
    const searchQuery = query

    return helpers.getEncodedQueryString({ query: searchQuery })
  }

  buildResultsHref (encodedQueryString) {
    if (!encodedQueryString) return null

    return `${this.searchPath}?${encodedQueryString}`
  }

  buildSearchLink (queryString) {
    if (typeof queryString !== 'string' || !queryString) return null

    return `${this.searchPath}?${queryString}`
  }

  buildListMarkupFromQueries (items = []) {
    const escapeHtml = (s) =>
      String(s)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;')

    return items
      .map(({ displayString, queryString, link }) => {
        const href = link || this.buildSearchLink(queryString)
        if (!href) return ''

        return `
        <li class="drw-QuickSearch_SuggestionItem">
          <a href="${href}" class="drw-QuickSearch_SuggestionLink fz-13_143">${escapeHtml(displayString || '')}</a>
        </li>
      `
      })
      .join('')
  }

  buildCollectionResults (container, response) {
    const sections = Array.isArray(response?.queries) ? response.queries : []
    const collections = sections.find(
      (section) => section?.section_id === 'collections',
    )

    const items = Array.isArray(collections?.items) ? collections.items : []
    const html = this.buildListMarkupFromQueries(items)

    if (container) {
      container.innerHTML = html
      this.els?.['suggestion-collections']?.element?.setAttribute(
        'aria-hidden',
        !items.length,
      )
    }

    return html
  }

  async buildRecentSearches (container, limit = 5) {
    const helpers = window.searchSuggestions?.helpers
    if (!helpers) return ''

    const recentSearches = await helpers.getRecentSearches(limit)

    if (recentSearches.length === 0) return

    const html = this.buildListMarkupFromQueries(recentSearches)

    container.innerHTML = html

    this.els['suggestion-recents']?.element?.setAttribute('aria-hidden', false)

    return html
  }

  async buildPopularSearches (container, limit = 5) {
    const helpers = window.searchSuggestions?.helpers
    if (!helpers) return ''

    const recentSearches = await helpers.getPopularSearches(limit)

    if (recentSearches.length === 0) return

    const html = this.buildListMarkupFromQueries(recentSearches)

    container.innerHTML = html

    this.els['suggestion-popular']?.element?.setAttribute('aria-hidden', false)

    return html
  }

  async buildProductResults (container, response) {
    const productsContainer = this.els?.['products-container']?.element
    if (!productsContainer) return ''

    const current = this._currentQuery || ''

    if (current.length < 3) {
      container.innerHTML = ''
      productsContainer.setAttribute('aria-hidden', true)
      productsContainer.removeAttribute('aria-busy')
      this.els?.footer?.element?.setAttribute('aria-hidden', true)
      this.setState('default')
      return ''
    }

    productsContainer.setAttribute('aria-hidden', false)
    productsContainer.setAttribute('aria-busy', 'true')

    const queryAtStart = current

    const productResultsHtml = await this.createProductCards(response.products)

    if (
      this._currentQuery !== queryAtStart ||
      (this._currentQuery || '').length < 3
    ) {
      productsContainer.setAttribute('aria-hidden', true)
      productsContainer.removeAttribute('aria-busy')
      this.setState('default')
      return ''
    }

    container.innerHTML = productResultsHtml

    const resultsTotal = response?.metadata?.products?.total || 0
    const encodedQuery = this.getEncodedQueryStringFromTagalys(current)
    const buttonHref = this.buildResultsHref(encodedQuery)

    if (resultsTotal > 0) {
      this.els?.footer?.element.setAttribute('aria-hidden', false)
      this.els['button-total'].element.innerHTML = translateString(
        this.translations?.quickSearch?.productsTotal,
        { count: `${resultsTotal}` },
      )
      this.els['button-total'].element.setAttribute('href', buttonHref)
      this.els['products-link'].element.setAttribute('href', buttonHref)

      this.setState('results')
    } else {
      this.els?.footer?.element.setAttribute('aria-hidden', true)

      this.els['no-results'].element.innerHTML = translateString(
        this.translations?.quickSearch?.noResults,
        { terms: `${current}` },
      )

      this.setState('no-results')
      if (!productResultsHtml.trim()) {
        productsContainer.setAttribute('aria-hidden', true)
      }
    }

    productsContainer.removeAttribute('aria-busy')

    return productResultsHtml
  }

  async createProductCards (products) {
    const cards = await Promise.all(
      products.map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`

        try {
          const res = await window.BAO.sectionRenderer.fetchSingle(
            'tagalys-product-card',
            productUrl,
          )
          const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
          const article = newDOM.querySelector('article')

          return article
            ? `
              <div class="drw-QuickSearch_QueryItem">
                ${article.outerHTML}
              </div>`
            : ''
        } catch (error) {
          this.handleError(error)
          return ''
        }
      }),
    )

    return cards.join('')
  }

  get searchPath () {
    return this.getAttribute('search-path') || '/pages/search'
  }

  get translations () {
    return window.theme.strings
  }
}

if (!customElements.get('tagalys-quick-search')) {
  customElements.define('tagalys-quick-search', TagalysQuickSearch)
}
